import { KyInstance } from 'ky';

import { VITE_PLUGIN_SERVICE_URL } from '@/constants/env';
import { handleRequest } from '@/utils/httpUtils';

import type {
  ConvertToMe1,
  ConvertToMe1Response,
  CreateOrder,
  CreateOrderResponse,
  DeletePlugin,
  GetPluginDetails,
  GetUserPlugins,
  Install,
  OrderById,
  OrderResponse,
  Orders,
  OrdersResponse,
  Products,
  ProductsResponse,
  Rate,
  RateResponse,
  SyncCatalog,
  SyncCatalogResponse,
  SyncProduct,
  SyncProductResponse,
  UpdatePlugin,
  UpdateWarehouses,
  UpdateWarehousesResponse,
  Warehouses,
  WarehousesResponse,
} from './types';

export interface IReqMLFulfill {
  orders: Orders;
  orderById: OrderById;
  rate: Rate;
  createOrder: CreateOrder;
  install: Install;
  getUserPlugins: GetUserPlugins;
  getPluginDetails: GetPluginDetails;
  deletePlugin: DeletePlugin;
  updatePlugin: UpdatePlugin;
  products: Products;
  syncProduct: SyncProduct;
  syncCatalog: SyncCatalog;
  warehouses: Warehouses;
  updateWarehouses: UpdateWarehouses;
  convertToMe1: ConvertToMe1;
}

export function reqMLFulfill(http: KyInstance): IReqMLFulfill {
  const route = 'api/v3/mercadolibre/fulfill';
  return {
    orders: ({ page, pageSize, status, dateFrom, dateTo, clientId }) =>
      handleRequest<OrdersResponse>(http, 'get', `${route}/orders`, {
        searchParams: {
          ...(page !== undefined && { page }),
          ...(pageSize !== undefined && { pageSize }),
          ...(status && { status }),
          ...(dateFrom && { dateFrom }),
          ...(dateTo && { dateTo }),
        },
        headers: { 'X-Client-ID': clientId },
      }),

    orderById: ({ orderId, clientId }) =>
      handleRequest<OrderResponse>(http, 'get', `${route}/orders/${orderId}`, {
        headers: { 'X-Client-ID': clientId },
      }),

    rate: ({ id, clientId }) =>
      handleRequest<RateResponse>(http, 'post', `${route}/orders/rate`, {
        json: { id },
        headers: { 'X-Client-ID': clientId },
      }),

    createOrder: ({ id, clientId }) =>
      handleRequest<CreateOrderResponse>(http, 'post', `${route}/orders/create`, {
        json: { id },
        headers: { 'X-Client-ID': clientId },
      }),

    install: ({ country, clientId }) => {
      const baseUrl = new URL(`${route}/install/${country}`, VITE_PLUGIN_SERVICE_URL);
      baseUrl.searchParams.set('X-Client-ID', String(clientId));
      window.location.href = baseUrl.toString();
      return Promise.resolve();
    },

    getUserPlugins: ({ clientId }) =>
      handleRequest(http, 'get', 'api/v3/user/plugins', {
        headers: { 'X-Client-ID': clientId },
      }),

    getPluginDetails: ({ clientId, pluginId }) =>
      handleRequest(http, 'get', `api/v3/user/plugins/${pluginId}/details`, {
        headers: { 'X-Client-ID': clientId },
      }),

    deletePlugin: ({ clientId, pluginId }) =>
      handleRequest(http, 'delete', `api/v3/user/plugins/${pluginId}/delete`, {
        headers: { 'X-Client-ID': clientId },
      }),

    updatePlugin: ({ clientId, pluginId, data }) =>
      handleRequest(http, 'patch', `api/v3/user/plugins/${pluginId}/update`, {
        json: data,
        headers: { 'X-Client-ID': clientId },
      }),

    products: ({ page, pageSize, clientId }) =>
      handleRequest<ProductsResponse>(http, 'get', `${route}/products`, {
        searchParams: {
          ...(page !== undefined && { page }),
          ...(pageSize !== undefined && { pageSize }),
        },
        headers: { 'X-Client-ID': clientId },
      }),

    syncProduct: ({ productId, clientId, height, width, length, weight }) =>
      handleRequest<SyncProductResponse>(http, 'post', `${route}/products/${productId}/sync`, {
        json: { height, width, length, weight },
        headers: { 'X-Client-ID': clientId },
      }),

    syncCatalog: ({ clientId, products }) =>
      handleRequest<SyncCatalogResponse>(http, 'post', `${route}/products/sync`, {
        json: { products },
        headers: { 'X-Client-ID': clientId },
      }),

    warehouses: ({ clientId }) =>
      handleRequest<WarehousesResponse>(http, 'get', `${route}/warehouses`, {
        headers: { 'X-Client-ID': clientId },
      }),

    updateWarehouses: ({ clientId, warehouseIds }) =>
      handleRequest<UpdateWarehousesResponse>(http, 'post', `${route}/warehouses`, {
        json: warehouseIds,
        headers: { 'X-Client-ID': clientId },
      }),

    convertToMe1: ({ productId, clientId, height, width, length, weight, localPickUp = false }) =>
      handleRequest<ConvertToMe1Response>(http, 'post', `${route}/products/${productId}/me1`, {
        json: { height, width, length, weight, localPickUp },
        headers: { 'X-Client-ID': clientId },
      }),
  };
}
